import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import React from 'react';
import { ReactReduxContext } from 'react-redux';
import { __RouterContext } from 'react-router';
import { useAtlasThemeVariant, type AtlasThemeVariant } from 'src/hooks';
import { plusJakartaFontFamilyStack } from 'src/theme/atlas';
import { buildTheme, oldTheme } from 'src/theme/theme';
import isPropValid from '@emotion/is-prop-valid';
import {
  createGlobalStyle,
  StyleSheetManager,
  ThemeProvider,
} from 'styled-components';

interface Props {
  children: React.ReactNode;
  /**
   * Force a specific theme variant, bypassing the FF hook (which requires a
   * Redux Provider). Intended for tests / Storybook. Production code should
   * omit this prop and let the hook resolve the variant from FF + route.
   */
  variant?: AtlasThemeVariant;
}

// Overrides all legacy Ubuntu/Rubik font-family rules (index.css, AuthenticationAdmin.css, etc.)
// so that every element uses Plus Jakarta Sans when Atlas FF is ON.
const AtlasGlobalFontStyle = createGlobalStyle`
  * {
    font-family: ${plusJakartaFontFamilyStack} !important;
  }
`;

const shouldForwardProp = (prop: string, target: unknown): boolean =>
  typeof target === 'string' ? isPropValid(prop) : true;

// ThemeProviders is a component that provides the theme to the app
export const ThemeProviders: React.FC<Props> = ({ children, variant }) => {
  return variant ? (
    <ThemeProvidersInner variant={variant}>{children}</ThemeProvidersInner>
  ) : (
    <ThemeProvidersFromHook>{children}</ThemeProvidersFromHook>
  );
};

// Guard component: checks that both Redux and v5-Router contexts are available before
// calling hooks that depend on them. Falls back to vault when either context is missing
// (e.g. isolated Cypress component tests that don't wrap in a full Provider tree).
const ThemeProvidersFromHook: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const hasRedux = !!React.useContext(ReactReduxContext);
  const hasRouter = !!React.useContext(__RouterContext);
  if (!hasRedux || !hasRouter) {
    return (
      <ThemeProvidersInner variant="vault">{children}</ThemeProvidersInner>
    );
  }
  return <ThemeProvidersFromHookInner>{children}</ThemeProvidersFromHookInner>;
};

// Inner component: only rendered when Redux + Router are guaranteed present.
const ThemeProvidersFromHookInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const variant = useAtlasThemeVariant();
  return (
    <ThemeProvidersInner variant={variant}>{children}</ThemeProvidersInner>
  );
};

const ThemeProvidersInner: React.FC<{
  children: React.ReactNode;
  variant: AtlasThemeVariant;
}> = ({ children, variant }) => {
  const selectedTheme = buildTheme(variant);
  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={oldTheme}>
          <ThemeProvider theme={selectedTheme}>
            {variant === 'atlas' && <AtlasGlobalFontStyle />}
            {children}
          </ThemeProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </StyleSheetManager>
  );
};
